// ---------------------------------------------------------------------------
// waveformPeaks — pure min/max peak extraction for the Director View waveform
// lane (DIR-003).
//
// The same reduction every waveform renderer uses (BBC audiowaveform,
// wavesurfer.js): fold the channels into one mono signal, slice it into equal
// buckets, and keep the extremes of each bucket. Drawing a vertical line from
// `min[i]` to `max[i]` reproduces the familiar envelope.
//
// Peaks are extracted once at a fixed high resolution and resampled down for
// whatever width the panel happens to be. That matters for memory: a decoded
// 90-second stereo AudioBuffer is ~32 MB, while its peak set is ~32 KB, so we
// take the peaks and drop the buffer immediately rather than keeping audio
// alive to redraw on resize.
// ---------------------------------------------------------------------------

export type WaveformPeaks = {
  /** Most negative sample per bucket, −1..0. */
  min: Float32Array;
  /** Most positive sample per bucket, 0..1. */
  max: Float32Array;
  buckets: number;
};

/**
 * Extraction resolution. Well above any realistic panel width so resampling is
 * always a downsample (and therefore lossless in the min/max sense).
 */
export const PEAK_RESOLUTION = 4096;

/**
 * Reduce decoded channel data to bucketed peaks. Channels are averaged, which
 * is what audiowaveform means by "combines input channels to produce a mono
 * signal" — a centre-panned narrator stays centred.
 */
export function computePeaks(
  channels: Float32Array[],
  buckets: number = PEAK_RESOLUTION,
): WaveformPeaks {
  const safeBuckets = Math.max(1, Math.floor(buckets));
  const min = new Float32Array(safeBuckets);
  const max = new Float32Array(safeBuckets);
  const frames = channels[0]?.length ?? 0;

  if (frames === 0 || channels.length === 0) {
    return { min, max, buckets: safeBuckets };
  }

  const channelCount = channels.length;
  // Fractional stride: distributing the remainder keeps the last bucket from
  // being a stub on inputs whose length is not a multiple of the bucket count.
  const stride = frames / safeBuckets;

  for (let bucket = 0; bucket < safeBuckets; bucket += 1) {
    const start = Math.floor(bucket * stride);
    const end =
      bucket === safeBuckets - 1 ? frames : Math.floor((bucket + 1) * stride);
    let bucketMin = 0;
    let bucketMax = 0;
    let seen = false;

    for (let frame = start; frame < end; frame += 1) {
      let sample = 0;
      for (let channel = 0; channel < channelCount; channel += 1) {
        sample += channels[channel][frame] ?? 0;
      }
      sample /= channelCount;
      if (!seen) {
        bucketMin = sample;
        bucketMax = sample;
        seen = true;
        continue;
      }
      if (sample < bucketMin) bucketMin = sample;
      if (sample > bucketMax) bucketMax = sample;
    }

    min[bucket] = bucketMin;
    max[bucket] = bucketMax;
  }

  return { min, max, buckets: safeBuckets };
}

/**
 * Resample a peak set to `buckets` for rendering. Downsampling keeps the
 * extremes of the covered source buckets, so transients survive; upsampling
 * (a panel wider than the extraction resolution) repeats the nearest bucket.
 */
export function resamplePeaks(
  peaks: WaveformPeaks,
  buckets: number,
): WaveformPeaks {
  const safeBuckets = Math.max(1, Math.floor(buckets));
  if (safeBuckets === peaks.buckets) return peaks;

  const min = new Float32Array(safeBuckets);
  const max = new Float32Array(safeBuckets);
  const stride = peaks.buckets / safeBuckets;

  for (let bucket = 0; bucket < safeBuckets; bucket += 1) {
    const start = Math.floor(bucket * stride);
    const end =
      bucket === safeBuckets - 1
        ? peaks.buckets
        : Math.max(start + 1, Math.floor((bucket + 1) * stride));
    let bucketMin = peaks.min[start] ?? 0;
    let bucketMax = peaks.max[start] ?? 0;
    for (let source = start + 1; source < end; source += 1) {
      if (peaks.min[source] < bucketMin) bucketMin = peaks.min[source];
      if (peaks.max[source] > bucketMax) bucketMax = peaks.max[source];
    }
    min[bucket] = bucketMin;
    max[bucket] = bucketMax;
  }

  return { min, max, buckets: safeBuckets };
}

/**
 * Largest absolute excursion in the set. Used to normalise drawing so a quietly
 * mastered mix still fills the lane instead of rendering as a flat line.
 */
export function peakAmplitude(peaks: WaveformPeaks): number {
  let amplitude = 0;
  for (let bucket = 0; bucket < peaks.buckets; bucket += 1) {
    const low = Math.abs(peaks.min[bucket]);
    const high = Math.abs(peaks.max[bucket]);
    if (low > amplitude) amplitude = low;
    if (high > amplitude) amplitude = high;
  }
  return amplitude;
}

/** Peak set covering `[startSeconds, endSeconds)` of a clip `durationSeconds` long. */
export function slicePeaksByTime(
  peaks: WaveformPeaks,
  durationSeconds: number,
  startSeconds: number,
  endSeconds: number,
): WaveformPeaks {
  if (durationSeconds <= 0) return peaks;
  const clampedStart = Math.max(0, Math.min(startSeconds, durationSeconds));
  const clampedEnd = Math.max(
    clampedStart,
    Math.min(endSeconds, durationSeconds),
  );
  const first = Math.floor((clampedStart / durationSeconds) * peaks.buckets);
  const last = Math.ceil((clampedEnd / durationSeconds) * peaks.buckets);
  const count = Math.max(1, last - first);
  return {
    min: peaks.min.slice(first, first + count),
    max: peaks.max.slice(first, first + count),
    buckets: count,
  };
}
